import { resolveStorefrontFeatures } from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { InheritOrCustomize } from "../components/InheritOrCustomize";
import { LoserPrizeImageForm, LoserPrizePreview } from "../components/LoserPrizeImageForm";
import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { useStorefrontConfig } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Battles on one storefront environment: the switch (`features.battles`, on the header, saved on
 * flip through `storefront.update` as its one key, which the server merges onto the other
 * switches) and what the loser prize looks like. The image is inherited from the platform
 * default until an editor customizes it; "Use platform default" goes back. Battles snapshot the
 * image when they are created, so a change reaches new battles only.
 */
export function StorefrontBattlesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const [customizing, setCustomizing] = useState(false);
  const { queryOptions, query, update } = useStorefrontConfig();

  const saveImage = useMutation(
    trpc.storefront.setLoserPrizeImage.mutationOptions({
      onSuccess: (saved) => {
        setCustomizing(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.imageUrl === null ? "Using the platform default." : "Loser prize image saved.",
        );
      },
    }),
  );

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, loserPrize, environment } = query.data;
  const enabled = resolveStorefrontFeatures(config.features).battles;
  const own = config.loserPrizeImageUrl;
  const canEdit = can("editor");
  const submitImage = (imageUrl: string | null) => saveImage.mutate({ imageUrl });

  return (
    <StorefrontSection
      title="Battles"
      description="Multiplayer box battles on this storefront, and the prize a non-winning player is handed. Off hides the battle pages and refuses new battles and seats; a battle already under way keeps going, because its entries are paid."
      environment={environment}
      aside={
        <SectionSwitch
          id="battles-enabled"
          label="Battles"
          checked={enabled}
          disabled={update.isPending || !canEdit}
          onCheckedChange={(checked) => update.mutate({ features: { battles: checked } })}
        />
      }
    >
      <InheritOrCustomize
        inherited={own === null}
        customizing={customizing}
        canEdit={canEdit}
        saving={saveImage.isPending}
        onCustomize={() => setCustomizing(true)}
        onCancel={() => setCustomizing(false)}
        onRevert={() => submitImage(null)}
        descriptions={{
          inherited:
            "This environment shows the platform's image for the loser prize. Customize it to show your own; new battles pick it up as they are created.",
          customizing: "Started from the platform default; nothing is saved until you save.",
          customized: "This environment's own image; the platform default no longer applies here.",
        }}
        inheritedSummary={
          <div className="flex items-center gap-4">
            <LoserPrizePreview
              imageUrl={loserPrize.platformDefaultImageUrl}
              className="size-24 shrink-0 rounded-md"
            />
            <p className="text-sm text-muted-foreground">
              {loserPrize.platformDefaultImageUrl
                ? loserPrize.platformDefaultImageUrl
                : "The platform has set no image yet, so shoppers see a placeholder."}
            </p>
          </div>
        }
      >
        {(revert) => (
          <LoserPrizeImageForm
            key={own ?? "unsaved"}
            imageUrl={own ?? loserPrize.platformDefaultImageUrl}
            canEdit={canEdit}
            saving={saveImage.isPending}
            onSave={submitImage}
            footer={revert}
          />
        )}
      </InheritOrCustomize>
    </StorefrontSection>
  );
}
